import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type PayoutKYCRecord } from '@/lib/adminApi';
import { Badge, Card } from '@/components/ui';
import { useAdminPermission } from '@/lib/useAdminPermission';
import { ApiError } from '@/lib/api';
import { dateTime } from '@/lib/adminFormat';

/** PayoutKYCControl — the gated payout-KYC VERIFY write for a driver. Shared by
 *  the Payout-KYC directory drawer + the Bank-KYC review-queue drawer.
 *
 *  RBAC: only finance + superadmin hold payouts.kyc_verify, so other roles see a
 *  read-only note. (admin-svc enforces the same matrix and 403s regardless —
 *  this is a UX affordance, not the security boundary.)
 *
 *  This is a REAL audited write now: admin-svc owns the driver_kyc verification
 *  columns (migration 037 — a back-office governance flag with no owning Go
 *  service, like admin_users / booking_statuses) and flips them directly.
 *  Verifying a driver's payout-KYC is what GATES their Azupay earnings cash-out.
 *  Already-verified records can be reverted to pending (e.g. a mistaken approval).
 *
 *  DEFENSIVE: until the driver_kyc table is in this Postgres, or until migration
 *  037 is applied, the endpoint returns a clean 503 whose reason we surface
 *  verbatim — so the control degrades honestly (a "not available yet" note)
 *  rather than a dead/lying button.
 *
 *  PROPS: `driverId` is always required. `record` is optional — the Payout-KYC
 *  directory + Bank-KYC review pages pass the full record so the control can
 *  show the CURRENT verified state + offer a revert; the Payouts page + driver
 *  drawer (which only carry a driver id, not the KYC record) omit it, so the
 *  control renders as a plain verify action (state shows after the first write). */
export function PayoutKYCControl({ driverId, record }: { driverId: number; record?: PayoutKYCRecord }) {
  const canVerify = useAdminPermission('payouts.kyc_verify');
  const qc = useQueryClient();
  const [note, setNote] = useState('');

  // Current state is known only when the full record is supplied (the two KYC
  // pages). Otherwise we can't pre-show it — default to the verify affordance.
  const isVerified = record?.verified === true || record?.verification_status === 'verified';

  const mutation = useMutation({
    mutationFn: (action: 'verify' | 'unverify') => adminApi.verifyPayoutKYC(driverId, action, note),
    onSuccess: () => {
      // Refresh both surfaces that read these records (the directory + the
      // review queue) so the new verification state shows without a manual reload.
      qc.invalidateQueries({ queryKey: ['admin', 'payout-kyc'] });
      qc.invalidateQueries({ queryKey: ['admin', 'payout-kyc-review'] });
      setNote('');
    },
  });

  if (!canVerify) {
    return (
      <Card>
        <div className="mb-1 flex items-center justify-between">
          <div className="text-sm font-medium text-slate-100">Payout-KYC review</div>
          <Badge tone={isVerified ? 'ok' : 'warn'}>{isVerified ? 'Verified' : 'Pending'}</Badge>
        </div>
        <p className="text-xs text-muted">
          Read-only — your role can view payouts but cannot verify a driver's payout-KYC. (That action
          requires the <code className="text-slate-400">finance</code> role.)
        </p>
      </Card>
    );
  }

  // A 503 (table not in Postgres yet / migration 037 not applied) is surfaced as
  // the precise reason from the API; a real failure shows its message. We treat
  // the 503 as an informational "pending wiring" state, not a red error.
  const apiErr = mutation.error instanceof ApiError ? (mutation.error as ApiError) : null;
  const notAvailable = apiErr?.status === 501 || apiErr?.status === 503;

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-100">
          {isVerified ? 'Payout-KYC verified' : 'Verify payout-KYC'}
        </div>
        <Badge tone={isVerified ? 'ok' : 'warn'}>{isVerified ? 'Verified' : 'Pending'}</Badge>
      </div>

      {isVerified ? (
        <p className="mb-3 text-xs text-muted">
          This driver's bank / PayID record is verified{record?.verified_at ? ` (${dateTime(record.verified_at)})` : ''} —
          they may cash out earnings via Azupay. You can revert it to pending if the details no longer match.
        </p>
      ) : (
        <p className="mb-3 text-xs text-muted">
          Approve this driver's bank / PayID record so they may cash out earnings via Azupay. The action
          is recorded in the audit trail.
        </p>
      )}

      <label className="block text-[11px] text-text-secondary">Note (optional — recorded in the audit trail)</label>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. bank statement matches licence name"
        className="input mt-1"
      />

      <div className="mt-3 flex items-center gap-3">
        {isVerified ? (
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate('unverify')}
            className="rounded-md border border-ink-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-ink-800 disabled:opacity-50"
          >
            {mutation.isPending ? 'Working…' : 'Revert to pending'}
          </button>
        ) : (
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate('verify')}
            className="btn-primary"
          >
            {mutation.isPending ? 'Verifying…' : 'Verify payout-KYC'}
          </button>
        )}
        {mutation.isSuccess ? (
          <span className="text-xs text-ok">{mutation.data?.message ?? 'Done.'}</span>
        ) : null}
        {notAvailable ? (
          <span className="text-xs text-warn">
            Not available yet — {apiErr?.message || 'the verification columns are not migrated yet.'}
          </span>
        ) : mutation.isError ? (
          <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
        ) : null}
      </div>
    </Card>
  );
}
